import { Button, Dialog, Input, Label, MessageStrip, Text } from "@ui5/webcomponents-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useErrorText } from "../errors/ApiErrorMessage";

export interface IdleLockDialogProps {
  open: boolean;
  /** Lifts the lock (called after a successful re-authentication or PIN check). */
  onUnlock: () => void;
  /**
   * `backOffice`: re-authenticate with Keycloak (P4: the back-office session ends after the idle time);
   * `pos`: the cashier unlocks with the POS PIN and the session is kept (P4, ADR-0003/D-10).
   * Without handlers the lock is UI-only and "Unlock" lifts it.
   */
  mode?: "backOffice" | "pos";
  /** Back office: Keycloak login in a popup; resolves `false` when the popup was blocked or closed. */
  reauthenticate?: () => Promise<boolean>;
  /** Back office fallback: full sign-in redirect (unsaved work is lost). */
  onSignIn?: () => void;
  /** POS: verifies the PIN on the server; rejects with the API error (`pos_pin_invalid`, `pos_pin_locked`). */
  verifyPin?: (pin: string) => Promise<void>;
  onSignOut?: () => void;
}

const lockModes = { pos: "pos", backOffice: "backOffice", ui: "ui" } as const;
const pinPattern = /^[0-9٠-٩]{6,8}$/u;

/** Arabic-Indic digits are accepted in the PIN (A5) and sent as Latin digits. */
function latinDigits(text: string): string {
  return text.replace(/[٠-٩]/gu, (digit) => String(digit.charCodeAt(0) - 0x0660));
}

/**
 * Lock screen shown after inactivity (P4). Unsaved work stays in memory underneath. Back office re-authenticates
 * through Keycloak (popup, so nothing underneath is lost; a full sign-in is the fallback); POS unlocks with the
 * cashier PIN without ending the session. Escape does not dismiss the lock.
 */
export function IdleLockDialog({
  open,
  onUnlock,
  mode = "backOffice",
  reauthenticate,
  onSignIn,
  verifyPin,
  onSignOut,
}: IdleLockDialogProps) {
  const { t } = useTranslation("ui");
  const describeError = useErrorText();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupFailed, setPopupFailed] = useState(false);
  const pos = mode === "pos" && verifyPin !== undefined;
  const backOffice = mode === "backOffice" && reauthenticate !== undefined;

  const reset = () => {
    setPin("");
    setError(null);
    setPopupFailed(false);
  };

  const unlockWithPin = async () => {
    if (!verifyPin || !pinPattern.test(pin)) {
      setError(t("idleLock.pinFormat"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyPin(latinDigits(pin));
      reset();
      onUnlock();
    } catch (caught) {
      setPin("");
      setError(describeError(caught).message);
    } finally {
      setBusy(false);
    }
  };

  const signInAgain = async () => {
    if (!reauthenticate) return;
    setBusy(true);
    try {
      if (await reauthenticate()) {
        reset();
        onUnlock();
      } else setPopupFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      headerText={t("idleLock.title")}
      data-testid="idle-lock"
      data-mode={pos ? lockModes.pos : backOffice ? lockModes.backOffice : lockModes.ui}
      className="positive-idle-lock"
      // Escape must not dismiss the lock.
      onBeforeClose={(event) => {
        if (event.detail.escPressed) event.preventDefault();
      }}
      footer={
        <div className="positive-dialog-footer">
          {pos && (
            <Button design="Emphasized" disabled={busy} onClick={() => void unlockWithPin()} data-testid="idle-unlock">
              {t("idleLock.unlock")}
            </Button>
          )}
          {backOffice && !popupFailed && (
            <Button design="Emphasized" disabled={busy} onClick={() => void signInAgain()} data-testid="idle-reauth">
              {t("idleLock.signInAgain")}
            </Button>
          )}
          {backOffice && popupFailed && onSignIn && (
            <Button design="Emphasized" onClick={onSignIn} data-testid="idle-sign-in">
              {t("idleLock.signInRedirect")}
            </Button>
          )}
          {!pos && !backOffice && (
            <Button design="Emphasized" onClick={onUnlock} data-testid="idle-unlock">
              {t("idleLock.unlock")}
            </Button>
          )}
          {onSignOut && (
            <Button design="Transparent" onClick={onSignOut} data-testid="idle-sign-out">
              {t("idleLock.signOut")}
            </Button>
          )}
        </div>
      }
    >
      <div className="positive-idle-lock-body">
        <Text>{pos ? t("idleLock.pinMessage") : backOffice ? t("idleLock.reauthMessage") : t("idleLock.message")}</Text>
        {pos && (
          <>
            <Label for="idle-pin" required>
              {t("idleLock.pin")}
            </Label>
            <Input
              id="idle-pin"
              type="Password"
              value={pin}
              maxlength={8}
              data-testid="idle-pin"
              onInput={(event) => {
                setPin(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") void unlockWithPin();
              }}
            />
          </>
        )}
        {popupFailed && (
          <MessageStrip design="Critical" hideCloseButton data-testid="idle-popup-failed">
            {t("idleLock.popupFailed")}
          </MessageStrip>
        )}
        {error !== null && (
          <MessageStrip design="Negative" hideCloseButton data-testid="idle-error">
            {error}
          </MessageStrip>
        )}
      </div>
    </Dialog>
  );
}
